import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router';
// @mui
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { Box, Link, Container, Typography, Card, Button } from '@mui/material';
// layouts
import LogoOnlyLayout from '../../layouts/LogoOnlyLayout';
// components
import Page from '../../components/Page';
import Markdown from '../../components/Markdown';
import Scrollbar from '../../components/Scrollbar';
import SettingsModeToggle from '../../components/SettingsModeToggle';
import LoadingBackgroundProcess from '../../components/LoadingBackgroundProcess';
// sections
import { NewPasswordForm } from '../../sections/auth/new-password';

import { postAcceptTerms } from '../../api/users';
import { getHomePath } from '../../routes/paths';
import useAuth from '../../hooks/useAuth';

// ----------------------------------------------------------------------

const termsAndConditionsContent = `

<h2 style='text-align:center; font-size:2.5rem'>Terms and Conditions</h2>

Welcome to SchoolKo, a product by Genzai Corp. By using our service, you are agreeing to comply with and be bound by the following terms and conditions of use, which together with our privacy policy govern Genzai Corp's relationship with you in relation to this service.

1. **Definitions:**
* "Service" refers to the School Management System and Learning Management System provided by Genzai Corp, under the product name SchoolKo.
* "User" refers to anyone accessing and using the service including but not limited to School Administrator, School Employees, Students, School Applicants, and Guardians.
* "Content" refers to all text, images, data, information, and other material the User may handle while using the service.

2. **Acceptance of Terms:**
By using the Service, the User agrees to abide by the terms and conditions set out in this agreement.

3. **Changes to the Terms:**
Genzai Corp reserves the right to modify the terms and conditions at any time. The User will be notified of any significant changes, and continued use of the service will signify acceptance of any adjusted terms.

4. **Service Access and Usage:**
* Users are granted limited access to use the services and its content in compliance with these Terms.
* Users agree not to interfere with the service or try to access it using a method other than the interface provided by Genzai Corp.
* Users must not attempt to reverse engineer, decompile, or disassemble the Service, except and only to the extent that such activity is expressly permitted by applicable law.

5. **User Responsibilities:**
* Users are responsible for maintaining the confidentiality of their login information and are fully responsible for all activities that occur under their account.
* Users are required to notify Genzai Corp immediately of any unauthorized use or breach of security.
* Users agree not to use the service in any way that causes, or may cause, damage to the Service or impairment of the availability or accessibility of the Service.

6. **Payment:**
* **Payment for the Service:** Payment for the use of the Service is governed by a separate agreement between the Educational Institution and Genzai Corp.
* **Internal Transactions:** The payment transaction for services between the Educational Institution and its students is solely their responsibility except when using the online payment services provided by SchoolKo. Genzai Corp. will not be held liable for any disputes, issues, or losses that may arise from these transactions.

7. **Third-Party Payment Gateways:**
* For transactions using the online payment services provided by SchoolKo, we may use third-party services (payment gateways). Genzai Corp. only serves as a mediator between the third-party payment gateway and the user, and does not hold any responsibility for the actual processing of payments.
* Genzai Corp's responsibilities as mediator include investigating the cause of any transaction errors, escalating issues to the payment gateway as necessary, and fixing or recovering from system failures related to payment processing as promptly as possible. However, Genzai Corp. does not guarantee recovery of lost transactions or resolution of payment disputes.

8. **Intellectual Property:**
* All Content displayed on the Service, excluding User Data, is owned by Genzai Corp and is protected by intellectual property laws.
* Users are not permitted to copy, modify, distribute, sell, or lease any part of the Service or included software, nor may they reverse engineer or attempt to extract the source code of that software.
* User Data, meaning data inputted by the user into the Service, is owned by the user and the educational institution with which they are affiliated.

9. **Governing Law:**
These Terms shall be governed by and interpreted according to the laws of the Republic of the Philippines.

10. **Contact:**
For any concerns about these terms, please contact us at contact@genzai.ph.

---

<h2 style='text-align:center; font-size:2.5rem'>Privacy Policy</h2>

At Genzai Corp, we are committed to protecting your privacy as you use our SchoolKo service.

1. **Information We Collect:**
* We collect information to provide and improve our service. This can include personal information that you provide us, such as your name, email address, and other relevant data. We also collect information about your interactions with our service.

2. **Use of Information:**
* We use the information we collect to provide, maintain, and improve our service, as well as to communicate with you and personalize your experience.

3. **User Data:**
* User Data inputted into the Service is owned by the User and the Educational Institution with which they are affiliated. Genzai Corp will not use this data for any purpose other than to provide the service and as otherwise outlined in this privacy policy.

4. **Information Sharing:**
* We do not share your personal information with third parties except as required by law, or with your explicit consent.

5. **Security:**
* We employ security measures to protect your information from access by unauthorized persons and against unlawful processing, accidental loss, destruction, and damage.

6. **Children’s Privacy:**
* We do not knowingly collect personal information from children under 13 years old without parental consent.

7. **Changes to Privacy Policy:**
* We may update our privacy policy periodically. We will notify you of any changes by posting the new privacy policy on this page.

8. **Contact Us:**
* If you have any questions or suggestions about our privacy policy, do not hesitate to contact us at privacy@genzai.ph.

`

// <h2 style='text-align:center; font-size:2.5rem'>Terms and Conditions</h2>
const clinicTermsAndConditionsContent = `
  
Welcome to Regenesis Clinic. By accessing or using our services, you agree to be bound by the following terms and conditions. Please read them carefully.

1. **General**
   - These terms and conditions govern your use of our dermatological services and products. By booking an appointment or purchasing products, you accept these terms in full.

2. **Appointments and Cancellations**
   - Appointments must be booked in advance. We require at least 24 hours notice for any cancellations or rescheduling. Failure to do so may result in a cancellation fee.

3. **Treatment Consent**
   - All treatments are carried out with your consent. It is essential to disclose any medical conditions or medications as they may affect your treatment.

4. **Payment and Refunds**
   - Payment is due at the time of service. We accept cash, credit, and debit cards. Refunds for services are not provided; however, products can be returned within 14 days for a full refund, provided they are unopened and in their original condition.

5. **Privacy Policy**
   - We are committed to protecting your privacy. Personal information collected is used only for the purpose of providing our services. We do not share your information with third parties without your consent.

6. **Liability**
   - While we strive to provide the highest standard of care, we cannot guarantee specific results. We are not liable for any adverse reactions or complications from treatments received at our clinic.

7. **Changes to Terms and Conditions**
   - We reserve the right to modify these terms and conditions at any time. The most current version will always be posted on our website.

8. **Contact Us**
   - If you have any questions or concerns about these terms and conditions, please contact us at [Clinic Contact Information].

By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.

`

const TermsContent = ({ forViewingOnly, handleAccept, noAgreeButton }) => {
  const navigate = useNavigate();
  return <Container>
    <Box sx={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      height: '100vh', maxWidth: 'md', margin: 'auto',
      padding: (theme) => theme.spacing(12, 0),
      mt: forViewingOnly ? -15 : 0,
    }}>

      <Typography variant='h3' margin='auto' mt={1.5} width={{ xs: '350px', md: 'initial' }} align='center'>Terms & Conditions and Privacy Policy</Typography>

      <Card sx={{ mt: 1.5 }}>
        <Scrollbar sx={{ p: 5, height: '100%' }}>
          <Markdown children={clinicTermsAndConditionsContent} />
        </Scrollbar>
      </Card>

      {!forViewingOnly && !noAgreeButton && <Box margin='auto' mt={1.5} sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography color='text.secondary' variant='body2' align='center'>By clicking "I Agree", you agree to the terms and conditions and privacy policy stated above.</Typography>
        <LoadingButton sx={{ textTransform: 'capitalize', margin: 'auto', mt: 0.5 }} onClick={handleAccept} variant='contained'> I agree</LoadingButton>
      </Box>}

      {noAgreeButton && <Button variant='outlined' onClick={() => navigate(-1)} sx={{ margin: 'auto', mt: 2, }}>
        Back
      </Button>}

    </Box>
  </Container>;
}

// ----------------------------------------------------------------------

export default function TermsAndConditions({ pathname, forViewingOnly = false, noAgreeButton = false }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false)
  const { getAuthMe } = useAuth()

  const handleAccept = async () => {
    setIsLoading(true)
    try {
      await postAcceptTerms()
      getAuthMe()
      navigate(getHomePath(pathname), { replace: true });
    } catch (error) {
      console.log(error)
    }
    setIsLoading(false)
  }

  if (forViewingOnly) return (<TermsContent forViewingOnly={forViewingOnly} />)

  return (
    <Page title="Terms and Conditions">
      <LogoOnlyLayout fullPage />

      <Box sx={{
        position: 'absolute',
        top: 30,
        right: 30,
      }}>

        <SettingsModeToggle />
      </Box>

      <TermsContent handleAccept={handleAccept} noAgreeButton={noAgreeButton} />

      <LoadingBackgroundProcess loading={isLoading} />
    </Page >
  );
}
